import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { confirmDialog } from 'primereact/confirmdialog';

import axiosInstance from '../../../config/Axios';
import { uploadToGcs } from '../../../lib/gcsUpload';
import { toggleLoader } from '../../../redux/Slices/Global/globalSlice';
import { useCustomToast } from '../../Contexts/ToastContext';
import { resolveDeviceOnline } from '../../../utils/helpers';
import {
  useCreatePromo, useUpdatePromo, createQrLink, updateQrLink, createMediaRecord,
} from '../../../queries/promos';
import { ar, toDraft, toPayload, newDraft, DAYPARTS, dayLabel, daypartLabel } from './promosLogic';
import MediaPickerDialog from './MediaPickerDialog';
import ShapeFrame, { L_MATCH_AREA } from './kit/ShapeFrame';
import {
  SHAPE_KEYS, SHAPE_ENABLED, SHAPE_SIZE, PLAYER_TRANSITION, SEQ_HOLD_MS, PALETTES, CURRENCIES, QR_TYPES, MAX_DESIGNS, TITLE_MAX, LINE_MAX, paletteById,
} from './kit/constants';
import { normalizeQrTarget, QrTargetError } from './kit/qrTarget';
import { checkImageSize, readImageSize } from './kit/sizeCheck';
import {
  DUR_OPTIONS, EVERY_OPTIONS, ROUND_OPTIONS, withCurrent, everyDisabled, evLabel, durLabel, roundLabel, timingSummary, countLabel,
} from './kit/timing';
import { newTemplate, templateFromStored, templateToStored, emptyItem } from './kit/templateDraft';
import { loadFonts, loadImage, renderDesign, canvasToBlob } from './kit/renderer';
import { qrContent, renderAndUploadDesigns } from './kit/savePipeline';

/**
 * عروضي — the editor screen. Own route (/apps/promos/new, /apps/promos/:id),
 * sticky live preview, four steps (الشكل، التصميم، الشاشات، التوقيت), and the save
 * bar lives ONLY here. Timing fields map 1:1 onto the existing promo columns
 * (see kit/timing.js); this component never touches how/when a promo plays.
 *
 * Preview = the same canvas the save step renders and uploads (kit/renderer.js),
 * painted through ShapeFrame at the player's real geometry.
 */

const hhmm = (v) => (v ? v.slice(0, 5) : '');
const toSec = (v) => (v && v.length === 5 ? `${v}:00` : v);
const letter = (s) => ([...(s || '')][0] || '');
const ZERO_DAYS = [0, 1, 2, 3, 4, 5, 6];

export function initEditorDraft(promo, libraryById, qrLinks, seed = null, logoUrl = null, companyName = '') {
  const src = promo || seed; // seed = "copy of": same content, brand-new promo (own QR link, own rendered files)
  const base = src ? { ...toDraft(src, libraryById), ...(seed ? { id: null } : {}) } : newDraft();
  const stored = src?.template ? templateFromStored(src.template, seed ? [] : (src.media_ids || [])) : null;
  const tpl = stored || newTemplate({ hasLogo: !!logoUrl, venueName: companyName });
  if (stored && !stored.venueName) stored.venueName = String(companyName || '').slice(0, 18);
  if (stored && stored.qr.linkId) {
    const link = (qrLinks || []).find((l) => l.id === stored.qr.linkId);
    if (link) { tpl.qr.input = link.display || link.target_url; tpl.qr.display = link.display; }
    if (seed) tpl.qr.linkId = null;
  }
  if (seed && stored) tpl.items = tpl.items.map((it) => ({ ...it, mediaId: null, dirty: true }));
  const d = {
    ...base,
    name: src?.name || '',
    tab: src ? (stored ? 'design' : 'upload') : 'design',
    tpl,
    sel: 0,
    intervalSec: src ? src.interval_seconds : 60,
    show: src ? src.show_duration_seconds : 10,
    rounds: src?.manual_round_count || 3,
    ...(src ? {} : { part: 'eve', from: '17:00:00', to: '23:00:00', auto: true }),
    refreshCaption: false,
    isNew: !promo,
    logoUrl: logoUrl || null,
    storedLogo: stored ? stored.logo : undefined,
  };
  if (promo && stored) d.sig0 = designSig({ ...d, logoUrl: stored.logo === undefined ? (logoUrl || null) : stored.logo }, qrLinks);
  return d;
}

function currentLink(d, qrLinks) {
  const l = (qrLinks || []).find((x) => x.id === d.tpl.qr.linkId);
  return l && l.type === d.tpl.qr.type ? l : null;
}

/** Everything shared by all designs: when this changes, every design must be re-rendered. */
function designSig(d, qrLinks) {
  const l = currentLink(d, qrLinks);
  return JSON.stringify({ shape: d.shape, cur: d.tpl.currency, qt: d.tpl.qr.type, lk: l ? l.short_url : null, ss: d.tpl.qr.ssid, lg: d.logoUrl || null, br: d.tpl.brand || null, vn: d.tpl.brand === 'name' ? (d.tpl.venueName || '').trim() : null });
}

function qrParse(d) {
  const q = d.tpl.qr;
  if (d.shape !== 'lshape' || q.type === 'none') return { kind: 'none', ok: true }; // the bar and the corner carry no QR
  if (q.type === 'wifi') return { kind: 'wifi', ok: !!q.ssid.trim() };
  try { return { kind: 'link', ok: true, n: normalizeQrTarget(q.type, q.input) }; } catch (e) {
    return { kind: 'link', ok: false, err: e instanceof QrTargetError ? e.code : 'invalid' };
  }
}

export default function PromoEditor({ promo, seed = null, devices, libraryById, qrLinks, onClose, onSaved }) {
  const { t } = useTranslation();
  const tt = (k, o) => t(`promos.v2_${k}`, o);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { showToast } = useCustomToast();
  const company = useSelector((s) => s.global.companyDetails)?.company || {};
  const createPromo = useCreatePromo();
  const updatePromo = useUpdatePromo();

  const [d, setD] = useState(() => initEditorDraft(promo, libraryById, qrLinks, seed, company.logo, company.name));
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(null); // null | {i, n}
  const [pickerFor, setPickerFor] = useState(null); // 'photo' | 'upload'
  const [previewSrc, setPreviewSrc] = useState(null);
  const [rev, setRev] = useState(0);
  const [previewErr, setPreviewErr] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [seqShown, setSeqShown] = useState(undefined); // undefined = static preview; true/false = a sequence is playing (design on / off screen)
  const [seqIdx, setSeqIdx] = useState(0);
  const [sizes, setSizes] = useState({});
  const [upSel, setUpSel] = useState(0);
  const [logoBusy, setLogoBusy] = useState(false);
  // The logo URL the server just returned: used at once, because the app-wide company details (redux) only catch up after the refetch below.
  const [freshLogo, setFreshLogo] = useState(null);
  const [logoWarn, setLogoWarn] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [focusReq, setFocusReq] = useState(null);
  const [miniShown, setMiniShown] = useState(false);
  const pvRef = useRef(null);
  const curLogo = freshLogo || company.logo || null;
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const imgCache = useRef(new Map());
  const savedOnce = useRef(false);

  const upd = useCallback((patch) => { setD((x) => ({ ...x, ...patch })); setTouched(true); }, []);
  const updTpl = useCallback((patch) => { setD((x) => ({ ...x, tpl: { ...x.tpl, ...patch } })); setTouched(true); }, []);
  const updItem = (i, patch) => {
    setD((x) => ({ ...x, tpl: { ...x.tpl, items: x.tpl.items.map((it, k) => (k === i ? { ...it, ...patch, dirty: true } : it)) } }));
    setTouched(true);
  };
  const updQr = (patch) => updTpl({ qr: { ...d.tpl.qr, ...patch } });

  const shape = d.shape;
  const enabledShape = SHAPE_ENABLED[shape];
  const item = d.tpl.items[Math.min(d.sel, d.tpl.items.length - 1)];
  const venue = useMemo(() => ({ name: company.name || '', initial: letter(company.name), logoUrl: curLogo }), [company.name, curLogo]);
  const link = currentLink(d, qrLinks);
  const qp = qrParse(d);
  // The designs were rendered with a different logo than the venue's current one (companies.logo is the single source).
  const staleLogo = !!promo?.template && d.storedLogo !== undefined && (d.storedLogo || null) !== curLogo;
  const destChanged = !!(link && qp.kind === 'link' && qp.ok && qp.n.url.replace(/\/$/, '') !== (link.target_url || '').replace(/\/$/, ''));
  const apiHost = (axiosInstance.defaults?.baseURL || '').replace(/\/api\/?$/, '') || window.location.origin;

  /* photo urls for items restored from the stored template (only the media id is stored) */
  useEffect(() => {
    if (!libraryById || libraryById.size === 0) return;
    setD((x) => {
      let changed = false;
      const items = x.tpl.items.map((it) => {
        if (it.photo?.id && !it.photo.url) {
          const m = libraryById.get(it.photo.id);
          if (m) { changed = true; return { ...it, photo: { id: it.photo.id, url: m.path, name: m.file_name } }; }
        }
        return it;
      });
      return changed ? { ...x, tpl: { ...x.tpl, items } } : x;
    });
  }, [libraryById]);

  /* ---------- live preview (the very canvas that gets uploaded) ---------- */
  const renderItem = useCallback(async (it) => {
    await loadFonts([venue.name, it.title, it.line, it.price]);
    const getImg = async (url) => {
      if (!url) return null;
      if (!imgCache.current.has(url)) imgCache.current.set(url, loadImage(url).catch(() => null));
      return imgCache.current.get(url);
    };
    const [photo, logo] = await Promise.all([getImg(it.photo?.url), getImg(venue.logoUrl)]);
    let qr = null;
    try {
      const l = currentLink(d, qrLinks);
      qr = qrContent(shape, { ...d.tpl.qr, wifiPass: d.tpl.qr.wifiPass || 'x' },
        l ? { short_url: l.short_url, display: d.refreshCaption && qp.ok && qp.n ? qp.n.display : l.display } : (qp.kind === 'link' && qp.ok ? { display: qp.n.display } : null), apiHost);
      if (qp.kind === 'link' && !qp.ok) qr = null;
      if (qp.kind === 'wifi' && !qp.ok) qr = null;
    } catch (e) { qr = null; }
    return renderDesign({
      shape, palette: it.palette, currency: d.tpl.currency,
      item: { title: it.title.trim() || tt('ph_title'), line: it.line.trim(), price: it.price.trim(), palette: it.palette },
      venue: { name: venue.name, initial: venue.initial, logo, venueName: d.tpl.venueName || venue.name }, brand: d.tpl.brand || undefined, photo, qr,
    });
  }, [shape, d.tpl.brand, d.tpl.venueName, d.tpl.currency, d.tpl.qr, d.refreshCaption, qrLinks, venue, apiHost]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (d.tab !== 'design' || !enabledShape || playing) return undefined;
    let cancelled = false;
    const h = setTimeout(async () => {
      try {
        const cv = await renderItem(item);
        if (!cancelled) { setPreviewSrc(cv); setRev((r) => r + 1); setPreviewErr(false); }
      } catch (e) { if (!cancelled) setPreviewErr(true); }
    }, 120);
    return () => { cancelled = true; clearTimeout(h); };
  }, [d.tab, enabledShape, playing, item, renderItem]);

  // «شاهد التسلسل»: one APPEARANCE per design, in order, the way the player runs them (kit/constants.js PLAYER_TRANSITION): the design comes in with the
  // player's own transition, is held (compressed: SEQ_HOLD_MS, the same hold the template mode always used instead of show_duration_seconds), goes out;
  // then the next design. Same runner for designed templates and uploaded designs, on every shape. A single design still plays (the player animates it).
  const playable = d.tab === 'design' ? d.tpl.items.length : d.media.filter((m) => m.creative_url).length;
  const playSeq = async () => {
    if (playing || playable < 1) return;
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    const ms = (PLAYER_TRANSITION[shape] || PLAYER_TRANSITION.lshape).ms;
    const fromDesign = d.tab === 'design';
    const keepSel = fromDesign ? d.sel : upSel;
    const order = fromDesign ? d.tpl.items.map((_, k) => k) : d.media.map((m, k) => (m.creative_url ? k : -1)).filter((k) => k >= 0);
    setPlaying(true); setSeqShown(false);
    try {
      for (let n = 0; n < order.length; n += 1) {
        const k = order[n];
        setSeqIdx(n);
        setSeqShown(false); // the next design starts off screen
        if (fromDesign) {
          // eslint-disable-next-line no-await-in-loop
          const cv = await renderItem(d.tpl.items[k]);
          setPreviewSrc(cv); setRev((r) => r + 1); setD((x) => ({ ...x, sel: k }));
        } else setUpSel(k);
        // eslint-disable-next-line no-await-in-loop
        await wait(40);
        setSeqShown(true);
        // eslint-disable-next-line no-await-in-loop
        await wait(ms + SEQ_HOLD_MS);
        setSeqShown(false);
        // eslint-disable-next-line no-await-in-loop
        await wait(ms);
      }
    } finally {
      setPlaying(false); setSeqShown(undefined);
      if (fromDesign) setD((x) => ({ ...x, sel: keepSel })); else setUpSel(keepSel);
    }
  };

  /* ---------- failed save: bring the first invalid field into view and focus it ---------- */
  useEffect(() => {
    if (!focusReq) return undefined;
    const el = document.querySelector(focusReq.sel) || document.querySelector('.promos-shell .step .err');
    if (!el) return undefined;
    if (el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const tm = setTimeout(() => { if (el.focus) el.focus({ preventScroll: true }); }, 280);
    return () => clearTimeout(tm);
  }, [focusReq]);

  /* ---------- phone: the mini preview appears once the big preview has scrolled away ---------- */
  useEffect(() => {
    const el = pvRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setMiniShown(!e.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  /* ---------- upload tab: sizes ---------- */
  useEffect(() => {
    d.media.forEach((m) => {
      if (m.creative_url && !sizes[m.id]) {
        readImageSize(m.creative_url).then((s) => setSizes((x) => ({ ...x, [m.id]: s })));
      }
    });
  }, [d.media]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- uploads (device files / logo) ---------- */
  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => /^image\/(png|jpe?g)$/i.test(f.type));
    if (!list.length) { showToast('warn', tt('title'), tt('up_types')); return; }
    const companyId = company.id || localStorage.getItem('company_id');
    const added = [];
    for (const f of list) {
      try {
        const ext = f.type.includes('png') ? 'png' : 'jpg';
        const key = `${companyId}/root/${Math.random().toString(36).slice(2, 14)}${Date.now().toString(36)}.${ext}`;
        // eslint-disable-next-line no-await-in-loop
        const url = await uploadToGcs(f, key);
        // eslint-disable-next-line no-await-in-loop
        const id = await createMediaRecord({ name: f.name, size: f.size, path: url });
        // eslint-disable-next-line no-await-in-loop
        const s = await readImageSize(f);
        setSizes((x) => ({ ...x, [id]: s }));
        added.push({ id, file_name: f.name, creative_url: url, source: 'device' });
      } catch (e) { showToast('error', tt('title'), tt('up_failed', { name: f.name })); }
    }
    if (added.length) upd({ media: [...d.media, ...added] });
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    // Far from square (0.8-1.25) is almost certainly not a logo: warn, but still allow the upload.
    try { const sz = await readImageSize(file); const r = sz.w && sz.h ? sz.w / sz.h : 1; setLogoWarn(r < 0.8 || r > 1.25); } catch (e) { setLogoWarn(false); }
    try {
      const fd = new FormData(); fd.append('logo', file);
      const res = await axiosInstance.post('/company/logo', fd);
      if (res?.data?.data?.logo_url) setFreshLogo(res.data.data.logo_url);
      dispatch(toggleLoader('companyDetails'));
      // toggleLoader alone refreshes nothing: re-read /company/details so every surface (sidebar, Settings, renders) sees companies.logo.
      await queryClient.invalidateQueries('getUserSubscription');
      showToast('success', tt('title'), tt('logo_saved'));
    } catch (e) {
      showToast('error', tt('title'), e?.response?.data?.message || tt('logo_failed'));
    } finally { setLogoBusy(false); if (logoRef.current) logoRef.current.value = ''; }
  };

  /* ---------- validation ---------- */
  const willRenderAll = d.isNew || !promo?.template || designSig({ ...d, logoUrl: curLogo }, qrLinks) !== d.sig0 || d.refreshCaption;
  const anyRender = willRenderAll || d.tpl.items.some((i) => i.dirty || !i.mediaId);
  // First problem with the form, or null. `sel` = the field to scroll to + focus on a failed save; `item` = the design it belongs to.
  const invalid = (() => {
    if (!enabledShape) return { msg: tt('blk_shape'), sel: '.shapes' };
    if (d.tab === 'design') {
      const ti = d.tpl.items.findIndex((i) => !i.title.trim());
      if (ti >= 0) return { msg: tt('blk_title'), sel: '#fTitle', item: ti };
      const pi = shape === 'corner' ? d.tpl.items.findIndex((i) => !i.photo) : -1; // the corner shows the product photo, never the logo
      if (pi >= 0) return { msg: tt('blk_photo'), sel: '#fPhotoBtn', item: pi };
      if (qp.kind === 'link' && !qp.ok) return { msg: qp.err ? tt(`qre_${qp.err}`) : tt('qre_empty'), sel: '#fQrInput' };
      if (qp.kind === 'wifi' && (!qp.ok || (!d.tpl.qr.wifiPass && anyRender))) return { msg: tt('blk_wifi'), sel: d.tpl.qr.ssid.trim() ? '#fWpass' : '#fSsid' };
    } else if (d.media.length === 0) return { msg: tt('blk_media'), sel: '#upArea' };
    if (d.deviceIds && d.deviceIds.length === 0) return { msg: tt('blk_devices'), sel: '#devAll' };
    if (d.auto && d.part === 'custom' && (!d.from || !d.to)) return { msg: tt('blk_time'), sel: '#tFrom' };
    if (everyDisabled(d.intervalSec, d.show)) return { msg: tt('ev_err'), sel: 'input[name="ev"]:checked' };
    if (!d.auto && !(d.rounds >= 1)) return { msg: tt('blk_rounds'), sel: 'input[name="rd"]' };
    return null;
  })();
  // Nothing is red until the first «حفظ» tap (prototype v8: `attempted`); after that the message and fields follow the form live.
  const showErr = attempted && !!invalid;

  /* ---------- save ---------- */
  const onSave = async () => {
    if (saving) return;
    setAttempted(true);
    if (invalid) {
      // Failed save: message in the bar (via showErr), scroll to the first invalid field, switch to its design, focus it.
      if (invalid.item != null) setD((x) => ({ ...x, sel: invalid.item }));
      setFocusReq({ sel: invalid.sel, n: Date.now() });
      return;
    }
    setSaving({ i: 1, n: d.tpl.items.length });
    try {
      let mediaIds; let template = null; let linkRes = null;
      const companyId = company.id || localStorage.getItem('company_id');
      if (d.tab === 'design') {
        const q = d.tpl.qr;
        const counted = qp.kind === 'link';
        if (counted) {
          if (link) linkRes = destChanged ? await updateQrLink(link.id, { input: q.input }) : link;
          else linkRes = await createQrLink({ type: q.type, input: q.input, promo_id: promo?.id || null });
        }
        const qr = qrContent(shape, q, linkRes ? { short_url: linkRes.short_url, display: d.refreshCaption || !link ? linkRes.display : link.display } : null, apiHost);
        const sigNow = designSig({ ...d, logoUrl: curLogo, tpl: { ...d.tpl, qr: { ...q, linkId: linkRes?.id || q.linkId } } }, linkRes ? [...(qrLinks || []).filter((x) => x.id !== linkRes.id), linkRes] : qrLinks);
        const forceAll = !promo?.template || sigNow !== d.sig0 || d.refreshCaption || !!q.wifiPass;
        const res = await renderAndUploadDesigns(
          { shape, tpl: d.tpl, venue, qr, forceAll, companyId },
          { loadFonts, loadImage, renderDesign, canvasToBlob, uploadToGcs, createMediaRecord, onProgress: (i, n) => setSaving({ i: i + 1, n }) }
        );
        mediaIds = res.mediaIds;
        template = templateToStored({ ...d.tpl, items: res.items, qr: { ...q, linkId: linkRes?.id || null, display: linkRes?.display || q.display } }, shape, curLogo);
      } else {
        mediaIds = d.media.map((m) => m.id);
      }
      const payload = {
        ...toPayload({ ...d, name: (d.name || '').trim() || (d.tab === 'design' ? d.tpl.items[0].title.trim() : (d.media[0]?.file_name || '')), media: mediaIds.map((id) => ({ id })), everyMin: 1 }),
        interval_seconds: d.intervalSec,
        template,
      };
      if (d.isNew && !d.auto) payload.is_active = false;
      setSaving({ phase: 'record' }); // every design image is uploaded; now the promo record that points to them
      const res = promo
        ? await updatePromo.mutateAsync({ id: promo.id, ...payload })
        : await createPromo.mutateAsync(payload);
      const savedId = promo?.id || res?.data?.data?.id || res?.data?.id;
      if (linkRes && savedId && linkRes.promo_id !== savedId) await updateQrLink(linkRes.id, { promo_id: savedId });
      // The list resolves a card's picture from the promo + the media library. Both were fetched BEFORE this save, so
      // leaving now showed an empty pitch until a manual refresh. Re-read them and wait, THEN go back to the list.
      await Promise.all([
        queryClient.invalidateQueries('venueOverlayPromos'),
        queryClient.invalidateQueries('promoDesignLibrary'),
        queryClient.invalidateQueries('promoQrLinks'),
      ]);
      savedOnce.current = true;
      showToast('success', t('promos.title'), t('promos.toast_saved'));
      onSaved();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[promos] save failed', e);
      showToast('error', t('promos.title'), e?.response?.data?.message || (e?.message === 'logo_load_failed' ? tt('logo_load_failed') : tt('save_failed')));
    } finally { setSaving(null); }
  };

  const close = () => {
    if (!touched || savedOnce.current) { onClose(); return; }
    confirmDialog({
      message: tt('discard_msg'), header: tt('discard_title'), acceptLabel: tt('discard_yes'), rejectLabel: tt('discard_no'),
      acceptClassName: 'p-button-danger', accept: onClose,
    });
  };

  /* ---------- design-tab helpers ---------- */
  const addItem = () => {
    if (d.tpl.items.length >= MAX_DESIGNS) return;
    setD((x) => ({ ...x, sel: x.tpl.items.length, tpl: { ...x.tpl, items: [...x.tpl.items, emptyItem(x.tpl.items[x.tpl.items.length - 1]?.palette)] } })); setTouched(true);
  };
  const delItem = () => {
    if (d.tpl.items.length < 2) return;
    setD((x) => ({ ...x, sel: Math.max(0, x.sel - 1), tpl: { ...x.tpl, items: x.tpl.items.filter((_, k) => k !== x.sel) } })); setTouched(true);
  };
  const moveItem = (dir) => {
    setD((x) => {
      const j = x.sel + dir;
      if (j < 0 || j >= x.tpl.items.length) return x;
      const items = [...x.tpl.items];
      [items[x.sel], items[j]] = [items[j], items[x.sel]];
      return { ...x, sel: j, tpl: { ...x.tpl, items } };
    }); setTouched(true);
  };
  const moveMedia = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= d.media.length) return;
    const m = [...d.media]; [m[i], m[j]] = [m[j], m[i]]; upd({ media: m });
  };

  /* ---------- devices ---------- */
  const allIds = devices.map((x) => x.id);
  const allChecked = d.deviceIds === null || (allIds.length > 0 && allIds.every((id) => d.deviceIds.includes(id)));
  const toggleAll = (on) => upd({ deviceIds: on ? null : [] });
  const toggleDev = (id, on) => {
    const cur = d.deviceIds === null ? allIds : d.deviceIds;
    const next = on ? [...new Set([...cur, id])] : cur.filter((x) => x !== id);
    upd({ deviceIds: next.length === allIds.length ? null : next });
  };
  const devChecked = (id) => d.deviceIds === null || d.deviceIds.includes(id);

  /* ---------- timing ---------- */
  const durOpts = withCurrent(DUR_OPTIONS, d.show);
  const evOpts = withCurrent(EVERY_OPTIONS, d.intervalSec);
  const rdOpts = withCurrent(ROUND_OPTIONS, d.rounds);
  const whenLabel = d.auto
    ? (d.part === 'custom' ? tt('when_custom', { from: ar(hhmm(d.from)), to: ar(hhmm(d.to)) }) : daypartLabel(t, d.part))
    : '';
  const designCount = d.tab === 'design' ? d.tpl.items.length : d.media.length;
  const setPart = (k) => {
    const dp = DAYPARTS.find((x) => x.k === k);
    if (k === 'custom') upd({ part: k, auto: true, from: d.from || DAYPARTS[0].from, to: d.to || DAYPARTS[0].to });
    else upd({ part: k, auto: true, from: dp.from, to: dp.to });
  };
  const setMode = (auto) => (auto
    ? upd({ auto: true, part: d.part === 'man' ? 'eve' : d.part, from: d.from || '17:00:00', to: d.to || '23:00:00' })
    : upd({ auto: false, part: 'man' }));

  const [lx, ly, lw, lh] = L_MATCH_AREA; // the match area on the 1920x1080 stage, as the preview draws it
  const lzBox = { left: `${(lx / 1920) * 100}%`, top: `${(ly / 1080) * 100}%`, width: `${(lw / 1920) * 100}%`, height: `${(lh / 1080) * 100}%` };
  const upSpec = tt(`up_spec_${shape}`);
  // One source for the big preview, the mini preview and the rows: the clamped selected upload (0 when the list is empty).
  const upIdx = Math.min(upSel, Math.max(d.media.length - 1, 0));
  const selMedia = d.media[upIdx];
  const miniEmpty = d.tab !== 'design' && d.media.length === 0;
  const miniIdx = d.tab === 'design' ? d.sel : upIdx;
  const title = promo ? tt('edit_title', { name: promo.name }) : tt('new_title');
  const saveLabel = d.isNew && !d.auto ? tt('save') : (d.isNew ? tt('save_run') : tt('save'));
  const savingLabel = saving ? (saving.phase ? tt('saving_short') : tt('saving', { i: ar(saving.i), n: ar(saving.n) })) : null;

  return (
    <section className="view-edit" aria-label={tt('editor_aria')}>
      <button className="minipv" type="button" hidden={!miniShown} aria-label={tt('mini_back')}
        onClick={() => pvRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
        <span className="mtv">
          {enabledShape && (d.tab === 'design'
            ? <ShapeFrame shape={shape} source={previewSrc} rev={rev} ariaLabel="" />
            : <ShapeFrame shape={shape} src={selMedia?.creative_url} ariaLabel="" />)}
        </span>
        <span className="mtx"><b>{tt('mini_t')}</b><small>{miniEmpty ? tt('mini_none') : tt('design_n', { i: ar(miniIdx + 1), n: ar(designCount) })}{d.tab === 'design' && item.title.trim() ? ` · ${item.title.trim()}` : ''}</small></span>
        <span className="mgo">↑ {tt('mini_go')}</span>
      </button>
      <div className="ehead">
        <button className="btn ghost back" type="button" onClick={close} aria-label={tt('back')}>→</button>
        <h1>{title}</h1>
      </div>
      <div className="ecols">
        <div className="pv" ref={pvRef}>
          {enabledShape && (d.tab === 'design'
            ? <ShapeFrame shape={shape} source={previewSrc} rev={rev} ariaLabel={tt('preview_aria')} enter={seqShown} />
            : <ShapeFrame shape={shape} src={selMedia?.creative_url} ariaLabel={tt('preview_aria')} enter={seqShown} />)}
          {!enabledShape && <div className="tvframe soon-frame"><div className="tv" /></div>}
          <div className="pvnote">
            {enabledShape && playable >= 1 && (
              <button className="btn" type="button" onClick={playSeq} disabled={playing}>▶ {tt('play_seq')}</button>
            )}
            {playing && <span className="seq-state">{tt('now_showing', { i: ar(seqIdx + 1), n: ar(playable) })}</span>}
            <span className="pvsize">{tt(`size_${shape}`)}</span>
            {previewErr && <span className="errmsg">{tt('preview_err')}</span>}
          </div>
        </div>

        <form className="steps" onSubmit={(e) => e.preventDefault()} noValidate>
          {/* ---------- 1 shape ---------- */}
          <div className="step">
            <h2><span className="n">{ar(1)}</span>{tt('step_shape')}</h2>
            <div className="shapes" role="radiogroup" aria-label={tt('step_shape')}>
              {SHAPE_KEYS.map((k) => (
                <label key={k} className={`pick${SHAPE_ENABLED[k] ? '' : ' is-soon'}`} aria-disabled={!SHAPE_ENABLED[k]}>
                  <input type="radio" name="shape" value={k} checked={shape === k} disabled={!SHAPE_ENABLED[k]}
                    onChange={() => upd({ shape: k })} />
                  <ShapeGlyph k={k} />
                  <b>{tt(`shape_${k}`)}</b>
                  <small>{SHAPE_ENABLED[k] ? tt(`shape_${k}_sub`) : <span className="soon-badge">{tt('soon')}</span>}</small>
                </label>
              ))}
            </div>
            <p className="note">{tt(`note_${shape}`)}</p>
            <div className="fld">
              <label htmlFor="pName">{tt('name_label')}</label>
              <input id="pName" className="in" maxLength={60} value={d.name} placeholder={tt('name_ph')} onChange={(e) => upd({ name: e.target.value })} />
            </div>
          </div>

          {/* ---------- 2 design ---------- */}
          <div className="step">
            <h2><span className="n">{ar(2)}</span>{tt('step_design')}</h2>
            <div className="tabs" role="tablist" aria-label={tt('step_design')}>
              <button className="tab" role="tab" type="button" aria-selected={d.tab === 'design'} onClick={() => upd({ tab: 'design' })}>{tt('tab_design')}</button>
              <button className="tab" role="tab" type="button" aria-selected={d.tab === 'upload'} onClick={() => upd({ tab: 'upload' })}>{tt('tab_upload')}</button>
            </div>

            {d.tab === 'design' && (
              <div className="steps inner">
                {staleLogo && <p className="note stale" role="status">{tt('stale_logo')}</p>}
                <div className="fld">
                  <span className="lbl" id="itemsLbl">{tt('items_lbl')}</span>
                  <div className="items" role="tablist" aria-labelledby="itemsLbl">
                    {d.tpl.items.map((it, i) => (
                      <button key={it.key} className="item" type="button" role="tab" aria-selected={i === d.sel} onClick={() => { setD((x) => ({ ...x, sel: i })); }}>
                        <span className="sq" style={{ background: `linear-gradient(135deg,${paletteById(it.palette).a},${paletteById(it.palette).a2})` }} />
                        {ar(i + 1)}. {it.title || tt('untitled')}
                      </button>
                    ))}
                    {d.tpl.items.length < MAX_DESIGNS && <button className="item add" type="button" onClick={addItem}>+ {tt('add_design')}</button>}
                  </div>
                  <div className="help"><span>{tt('items_help')}</span></div>
                </div>
                <div className="itemhead">
                  <b>{tt('design_n', { i: ar(d.sel + 1), n: ar(d.tpl.items.length) })}</b>
                  <span className="itemacts">
                    <button className="btn ghost" type="button" onClick={() => moveItem(-1)} disabled={d.sel === 0} aria-label={tt('move_up')}>↑</button>
                    <button className="btn ghost" type="button" onClick={() => moveItem(1)} disabled={d.sel === d.tpl.items.length - 1} aria-label={tt('move_down')}>↓</button>
                    <button className="btn ghost" type="button" onClick={delItem} disabled={d.tpl.items.length < 2}>{tt('del_design')}</button>
                  </span>
                </div>
                <div className="fld">
                  <label htmlFor="fTitle">{tt('f_title')}</label>
                  <input id="fTitle" className={`in${attempted && !item.title.trim() ? ' err' : ''}`} maxLength={TITLE_MAX} value={item.title} onChange={(e) => updItem(d.sel, { title: e.target.value })} />
                  <div className="help">
                    {attempted && !item.title.trim() ? <span className="errmsg">{tt('title_err')}</span> : <span />}
                    <span>{ar(item.title.length)} / {ar(TITLE_MAX)}</span>
                  </div>
                </div>
                <div className="fld">
                  <label htmlFor="fLine">{tt('f_line')}</label>
                  <input id="fLine" className="in" maxLength={LINE_MAX} value={item.line} onChange={(e) => updItem(d.sel, { line: e.target.value })} />
                  <div className="help"><span>{tt('optional')}</span><span>{ar(item.line.length)} / {ar(LINE_MAX)}</span></div>
                </div>
                <div className="two">
                  <div className="fld">
                    <label htmlFor="fPrice">{tt('f_price')}</label>
                    <input id="fPrice" className="in" inputMode="decimal" maxLength={8} value={item.price} placeholder={tt('price_ph')}
                      onChange={(e) => updItem(d.sel, { price: e.target.value.replace(/[^\d.]/g, '') })} />
                    <div className="help"><span>{tt('price_help')}</span></div>
                  </div>
                  <div className="fld">
                    <label htmlFor="fCur">{tt('f_cur')}</label>
                    <select id="fCur" className="in" value={d.tpl.currency} onChange={(e) => updTpl({ currency: e.target.value })}>
                      {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{tt(`cur_${c.code}`)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="fld">
                  <span className="lbl">{tt('f_photo')}</span>
                  <div className="media">
                    <div className="ph" style={item.photo?.url ? { backgroundImage: `url(${item.photo.url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined} />
                    <div className="grow"><b>{item.photo ? tt('photo_from_library') : tt('photo_none')}</b>{tt('photo_hint')}</div>
                    <button id="fPhotoBtn" className="btn" type="button" onClick={() => setPickerFor('photo')}>{item.photo ? tt('change') : tt('choose')}</button>
                    {item.photo && <button className="btn ghost" type="button" onClick={() => updItem(d.sel, { photo: null })}>{tt('remove')}</button>}
                  </div>
                  {attempted && shape === 'corner' && !item.photo && <span className="errmsg err" role="alert">{tt('blk_photo')}</span>}
                </div>
                <div className="fld">
                  <span className="lbl" id="palLbl">{tt('f_pal')}</span>
                  <div className="palettes" role="radiogroup" aria-labelledby="palLbl">
                    {PALETTES.map((p) => (
                      <label key={p.id} className="sw" title={tt(`pal_${p.id}`)} style={{ background: `linear-gradient(135deg,${p.bg1} 50%,${p.a} 50%)` }}>
                        <input type="radio" name="pal" key={`${item.key}-${p.id}`} value={p.id} checked={item.palette === p.id} aria-label={tt(`pal_${p.id}`)} onChange={() => updItem(d.sel, { palette: p.id })} />
                      </label>
                    ))}
                  </div>
                </div>
                {shape !== 'corner' && (
                  <>
                    <div className="shared" id="sharedHd"><b>{tt('shared_t')}</b><span>{shape === 'bottom' ? tt('shared_s_bar') : tt('shared_s')}</span></div>
                    <div className="fld" id="brandFld">
                      <span className="lbl" id="brandLbl">{tt('f_brand')}</span>
                      <div className="chips" role="radiogroup" aria-labelledby="brandLbl">
                        <label className="chip"><input type="radio" name="brand" value="logo" checked={d.tpl.brand === 'logo'} onChange={() => updTpl({ brand: 'logo' })} /><span>{tt('brand_logo')}</span></label>
                        <label className="chip"><input type="radio" name="brand" value="name" checked={d.tpl.brand === 'name'} onChange={() => updTpl({ brand: 'name' })} /><span>{tt('brand_name')}</span></label>
                      </div>
                      {d.tpl.brand === null && <p className="note">{tt('brand_legacy')}</p>}
                      {d.tpl.brand !== 'name' && (
                        <div className="media" id="logoRow">
                          <span className="ph logo">
                            {curLogo ? <img src={curLogo} alt="" className="logo-img" /> : letter(company.name)}
                          </span>
                          <div className="grow"><b>{curLogo ? tt('logo_set') : tt('logo_none')}</b>{curLogo ? tt('logo_hint_set') : tt('logo_hint')}</div>
                          <button className="btn" type="button" disabled={logoBusy} onClick={() => logoRef.current?.click()}>{curLogo ? tt('logo_change') : tt('logo_upload')}</button>
                          <input ref={logoRef} type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                        </div>
                      )}
                      {logoWarn && d.tpl.brand !== 'name' && <p className="note stale" role="status">{tt('logo_not_square')}</p>}
                      {d.tpl.brand === 'name' && (
                        <div id="nameRow">
                          <input className="in" id="fVenue" maxLength={18} value={d.tpl.venueName} aria-label={tt('venue_name_aria')} onChange={(e) => updTpl({ venueName: e.target.value })} />
                          <div className="help"><span>{tt('venue_name_help')}</span><span>{ar((d.tpl.venueName || '').length)} / {ar(18)}</span></div>
                        </div>
                      )}
                      <div className="help"><span>{shape === 'bottom' ? tt('brand_help_bar') : tt('brand_help')}</span></div>
                    </div>
                  </>
                )}
                {shape === 'lshape' && (
                  <div className="fld">
                    <span className="lbl">{tt('f_qr')}</span>
                    <div className="chips" role="radiogroup" aria-label={tt('f_qr')}>
                      {QR_TYPES.map((q) => (
                        <label key={q.key} className="chip"><input type="radio" name="qr" value={q.key} checked={d.tpl.qr.type === q.key}
                          onChange={() => updQr({ type: q.key, input: d.tpl.qr.type === q.key ? d.tpl.qr.input : '' })} /><span>{tt(`qr_${q.key}`)}</span></label>
                      ))}
                      <label className="chip"><input type="radio" name="qr" value="order" disabled /><span>{tt('qr_order')}</span></label>
                    </div>
                    {qp.kind === 'link' && (
                      <>
                        <input id="fQrInput" className={`in${!qp.ok && (attempted || d.tpl.qr.input) ? ' err' : ''}`} dir="ltr" value={d.tpl.qr.input} placeholder={tt(`qrph_${d.tpl.qr.type}`)}
                          aria-label={tt('qr_link_aria')} onChange={(e) => updQr({ input: e.target.value })} />
                        {!qp.ok && (attempted || d.tpl.qr.input) && <span className="errmsg">{tt(`qre_${qp.err || 'empty'}`)}</span>}
                        {qp.ok && <span className="qr-shown" dir="ltr">{qp.n.display}</span>}
                      </>
                    )}
                    {qp.kind === 'wifi' && (
                      <div className="two">
                        <input id="fSsid" className="in" dir="ltr" placeholder={tt('wifi_ssid')} aria-label={tt('wifi_ssid')} value={d.tpl.qr.ssid} onChange={(e) => updQr({ ssid: e.target.value })} />
                        <input id="fWpass" className="in" dir="ltr" placeholder={tt('wifi_pass')} aria-label={tt('wifi_pass')} value={d.tpl.qr.wifiPass} onChange={(e) => updQr({ wifiPass: e.target.value })} />
                      </div>
                    )}
                    {d.tpl.qr.type !== 'none' && (
                      <div className="help"><span>{d.tpl.qr.type === 'wifi' ? tt('qr_help_wifi') : tt('qr_help')}</span></div>
                    )}
                    {destChanged && (
                      <label className="note check-note">
                        <input type="checkbox" checked={d.refreshCaption} onChange={(e) => upd({ refreshCaption: e.target.checked })} /> {tt('qr_refresh_caption')}
                      </label>
                    )}
                  </div>
                )}
                {shape !== 'lshape' && <p className="note">{tt('no_qr_small')}</p>}
              </div>
            )}

            {d.tab === 'upload' && (
              <div>
                <div className="upload" id="upArea" tabIndex={-1}>
                  <b>{tt('up_title')}</b>
                  <span>{upSpec}</span>
                  <span>
                    {tt('up_more')}{' '}
                    <button className="btn ghost inline" type="button" onClick={() => fileRef.current?.click()}>{tt('up_device')}</button>
                    {' · '}
                    <button className="btn ghost inline" type="button" onClick={() => setPickerFor('upload')}>{tt('up_library')}</button>
                  </span>
                  <input ref={fileRef} type="file" hidden multiple accept="image/png,image/jpeg" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }} />
                </div>
                {shape === 'lshape' && <p className="note lzhint">{tt('lz_hint')}</p>}
                <div className="uplist" data-shape={shape}>
                  {d.media.map((m, i) => {
                    const sz = sizes[m.id];
                    const chk = sz ? checkImageSize(shape, sz.w, sz.h) : null;
                    return (
                      <div key={m.id} className={`upi${chk && !chk.ok ? ' bad' : ''}${i === upSel ? ' sel' : ''}`} onClick={() => setUpSel(i)}>
                        <span className="num">{ar(i + 1)}</span>
                        <span className="sq" style={m.creative_url ? { backgroundImage: `url(${m.creative_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
                          {shape === 'lshape' && <i className="lz" aria-hidden="true" style={lzBox}><b>{tt('lz_label')}</b></i>}
                        </span>
                        <span className="grow">
                          <span className="nm">{tt('design_k', { i: ar(i + 1) })}<i className="sep nmsep" aria-hidden="true">•</i></span>
                          {/* meta: each item is one unbreakable token (promos.css .tk); the dot lives INSIDE the preceding token, with the same space on both sides, so a wrap never starts a line with it.
                              Only the verdict tokens (.verdict) carry the good/bad colour. */}
                          <small>
                            <span className="tk">{m.source === 'device' ? tt('src_device') : tt('src_library')}{sz && <i className="sep" aria-hidden="true">•</i>}</span>
                            {sz ? (
                              <>
                                <span className="tk">{`${ar(sz.w)}×${ar(sz.h)}`}<i className="sep" aria-hidden="true">•</i></span>
                                {chk.ok ? (
                                  chk.reason === 'exact'
                                    ? <span className="tk verdict">{tt('sz_ok')}</span>
                                    : <><span className="tk verdict gap">{tt('sz_scaled_a')}</span><span className="tk verdict">{tt('sz_scaled_b')}</span></>
                                ) : (
                                  <>
                                    <span className="tk verdict szlong">{tt(`sz_bad_${shape}`)}</span>
                                    <span className="tk verdict szs">{tt('pk_bad')}<i className="sep" aria-hidden="true">•</i></span>
                                    <span className="tk verdict szs">{tt('sz_need', { w: ar(SHAPE_SIZE[shape].w), h: ar(SHAPE_SIZE[shape].h) })}</span>
                                  </>
                                )}
                              </>
                            ) : <span className="tk">{tt('sz_reading')}</span>}
                          </small>
                        </span>
                        <span className="itemacts">
                          <button className="btn ghost" type="button" aria-label={tt('move_up')} disabled={i === 0} onClick={(e) => { e.stopPropagation(); moveMedia(i, -1); }}>↑</button>
                          <button className="btn ghost" type="button" aria-label={tt('move_down')} disabled={i === d.media.length - 1} onClick={(e) => { e.stopPropagation(); moveMedia(i, 1); }}>↓</button>
                          <button className="btn ghost" type="button" aria-label={tt('remove')} onClick={(e) => { e.stopPropagation(); upd({ media: d.media.filter((_, k) => k !== i) }); }}>✕</button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ---------- 3 screens ---------- */}
          <div className="step">
            <h2><span className="n">{ar(3)}</span>{tt('step_screens')}</h2>
            <div className="devs">
              <label className="dev">
                <input id="devAll" type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} />
                <span className="ic"><TvIcon /></span>
                <span className="grow"><b>{tt('all_screens')}</b><small>{countLabel(t, 'screens', devices.length)} • {tt('all_screens_sub')}</small></span>
              </label>
              {devices.map((dv) => {
                const on = resolveDeviceOnline(dv.connectivity, dv.online);
                const where = dv.branch?.name || dv.location_name;
                return (
                  <label key={dv.id} className="dev">
                    <input type="checkbox" checked={devChecked(dv.id)} onChange={(e) => toggleDev(dv.id, e.target.checked)} />
                    <span className="ic"><TvIcon /></span>
                    <span className="grow">
                      <b>{dv.name}</b>
                      <small><span className="dot" style={{ background: on ? 'var(--jade)' : 'var(--slate)' }} />{on ? tt('dev_on') : tt('dev_off')}{where ? ` · ${where}` : ''}</small>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* ---------- 4 timing ---------- */}
          <div className="step">
            <h2><span className="n">{ar(4)}</span>{tt('step_timing')}</h2>
            <div className="tabs" role="radiogroup" aria-label={tt('mode_aria')}>
              <label className="tab mode"><input type="radio" name="mode" checked={d.auto} onChange={() => setMode(true)} /><span>{tt('mode_auto')}</span></label>
              <label className="tab mode"><input type="radio" name="mode" checked={!d.auto} onChange={() => setMode(false)} /><span>{tt('mode_manual')}</span></label>
            </div>
            {d.auto ? (
              <div className="fld"><span className="lbl">{tt('when_lbl')}</span>
                <div className="chips">
                  {DAYPARTS.filter((x) => x.k !== 'man').map((x) => (
                    <label key={x.k} className="chip"><input type="radio" name="when" checked={d.part === x.k} onChange={() => setPart(x.k)} /><span>{daypartLabel(t, x.k)}</span></label>
                  ))}
                </div>
                {d.part === 'custom' && (
                  <div className="two">
                    <div className="fld"><label htmlFor="tFrom">{tt('from')}</label><input id="tFrom" className="in" type="time" value={hhmm(d.from)} onChange={(e) => upd({ from: toSec(e.target.value) })} /></div>
                    <div className="fld"><label htmlFor="tTo">{tt('to')}</label><input id="tTo" className="in" type="time" value={hhmm(d.to)} onChange={(e) => upd({ to: toSec(e.target.value) })} /></div>
                  </div>
                )}
                <span className="lbl days-lbl">{tt('days_lbl')}</span>
                <div className="chips">
                  {ZERO_DAYS.map((i) => (
                    <label key={i} className="chip"><input type="checkbox" checked={d.days.includes(i)}
                      onChange={(e) => { const next = e.target.checked ? [...d.days, i].sort() : d.days.filter((x) => x !== i); if (next.length) upd({ days: next }); }} /><span>{dayLabel(t, i)}</span></label>
                  ))}
                </div>
              </div>
            ) : <p className="note">{tt('manual_note')}</p>}
            <div className="fld"><span className="lbl">{tt('dur_lbl')}</span>
              <div className="chips">
                {durOpts.map((v) => <label key={v} className="chip"><input type="radio" name="dur" checked={d.show === v} onChange={() => upd({ show: v })} /><span>{durLabel(t, v)}</span></label>)}
              </div>
            </div>
            <div className="fld"><span className="lbl">{tt('ev_lbl')}</span>
              <div className="chips">
                {evOpts.map((v) => (
                  <label key={v} className="chip"><input type="radio" name="ev" checked={d.intervalSec === v} disabled={everyDisabled(v, d.show)} onChange={() => upd({ intervalSec: v })} /><span>{evLabel(t, v)}</span></label>
                ))}
              </div>
              <div className="help">{showErr && everyDisabled(d.intervalSec, d.show) ? <span className="errmsg">{tt('ev_err')}</span> : <span />}</div>
            </div>
            {!d.auto && (
              <div className="fld"><span className="lbl">{tt('rd_lbl')}</span>
                <div className="chips">
                  {rdOpts.map((v) => <label key={v} className="chip"><input type="radio" name="rd" checked={d.rounds === v} onChange={() => upd({ rounds: v })} /><span>{roundLabel(t, v)}</span></label>)}
                </div>
                <div className="help"><span>{tt('rd_help')}</span></div>
              </div>
            )}
            <p className="note">{timingSummary(t, { manual: !d.auto, whenLabel, designs: designCount, dur: d.show, every: d.intervalSec, rounds: d.rounds })}</p>
          </div>
        </form>
      </div>

      <div className="savebar">
        <div className="in2">
          <span className={`msg${showErr ? ' bad' : ''}`} role="status">
            {savingLabel || (showErr ? invalid.msg : (touched ? tt('unsaved') : ''))}
          </span>
          <button className="btn" type="button" onClick={close} disabled={!!saving}>{tt('cancel')}</button>
          <button className="btn primary" type="button" onClick={onSave} disabled={!!saving}>{saving ? tt('saving_short') : saveLabel}</button>
        </div>
      </div>

      <MediaPickerDialog
        visible={!!pickerFor}
        onHide={() => setPickerFor(null)}
        t={t}
        single={pickerFor === 'photo'}
        shape={pickerFor === 'upload' ? shape : null}
        existingIds={new Set(pickerFor === 'upload' ? d.media.map((m) => m.id) : [])}
        onPickMany={(list) => {
          if (pickerFor === 'photo') {
            const m = list[0];
            if (m) updItem(d.sel, { photo: { id: m.id, url: m.creative_url, name: m.file_name } });
          } else upd({ media: [...d.media, ...list.map((m) => ({ ...m, source: 'library' }))] });
        }}
      />
    </section>
  );
}

function TvIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="12" rx="2" /><path d="M8 20h8" />
    </svg>
  );
}

function ShapeGlyph({ k }) {
  const box = (st) => <i style={st} />;
  if (k === 'lshape') return <div className="glyph">{box({ left: '80%', top: 0, width: '20%', height: '100%' })}{box({ left: 0, top: '80%', width: '100%', height: '20%' })}</div>;
  if (k === 'bottom') return <div className="glyph">{box({ left: 0, top: '80%', width: '100%', height: '20%' })}</div>;
  if (k === 'corner') return <div className="glyph">{box({ right: '5%', bottom: '8%', width: '20%', height: '34%', borderRadius: '50%' })}</div>;
  return (
    <div className="glyph" style={{ background: '#1C130A' }}>
      {box({ left: '4%', top: '9%', width: '58%', height: '58%', background: '#3D3446', borderRadius: 3 })}
      {box({ left: '66%', top: '9%', width: '30%', height: '58%', background: '#FFFDF8', borderRadius: 3 })}
      {box({ left: '4%', top: '74%', width: '92%', height: '16%', opacity: 0.6 })}
    </div>
  );
}

